import { Client } from "typesense";
import { Alpine } from "../../vendor/livewire/livewire/dist/livewire.esm";

document.addEventListener("livewire:navigating", () => {
    if (window.searchModalInstance) {
        window.searchModalInstance.isOpen = false;
        window.searchModalInstance.query = "";
        window.searchModalInstance.results = [];
        window.searchModalInstance.appResults = [];
        window.searchModalInstance.selectedUser = null;
        window.searchModalInstance.userMenuItems = [];
        window.searchModalInstance.showUserMenu = false;
        window.searchModalInstance._destroyed = true;
    }
});

Alpine.data(
    "spotlightSearch",
    ({
        clientCredentials,
        searchPrefix,
        appList,
        contextSearchEnabled = false,
    }) => {
        if (!window.typesenseClient && clientCredentials) {
            window.typesenseClient = new Client(clientCredentials);
        }

        return {
            isOpen: false,
            query: "",
            results: [],
            appResults: [],
            selectedIndex: 0,
            isLoading: false,
            searchPrefix,
            appList,
            contextSearchEnabled,
            selectedUser: null,
            userMenuItems: [],
            showUserMenu: false,
            _destroyed: false,

            init() {
                window.searchModalInstance = this;
                this._destroyed = false;

                this.$watch("query", async (value) => {
                    if (this._destroyed) return;

                    if (this.showUserMenu) {
                        this.selectedIndex = 0;
                        return;
                    }

                    if (!value || value.length < 2) {
                        this.results = [];
                        this.appResults = [];
                        this.selectedIndex = 0;
                        return;
                    }

                    await this.search(value);
                });
            },

            get filteredUserMenuItems() {
                if (!this.query) return this.userMenuItems;

                return this.userMenuItems.filter((item) =>
                    item.name.toLowerCase().includes(this.query.toLowerCase()),
                );
            },

            get allResults() {
                if (this.showUserMenu) {
                    return this.filteredUserMenuItems;
                }
                return [...(this.appResults ?? []), ...(this.results ?? [])];
            },

            get hasResults() {
                return this.allResults?.length > 0;
            },

            async search(query) {
                if (this._destroyed) return;

                this.isLoading = true;
                this.selectedIndex = 0;

                this.appResults = (this.appList ?? [])
                    .filter(({ name }) =>
                        name.toLowerCase().includes(query.toLowerCase()),
                    )
                    .map((app) => ({ ...app, type: "app" }));

                if (!window.typesenseClient) {
                    this.isLoading = false;
                    return;
                }

                try {
                    const response =
                        await window.typesenseClient.multiSearch.perform({
                            searches: [
                                {
                                    collection: this.searchPrefix + "students",
                                    q: query,
                                    query_by:
                                        "phone,birthday,prefixed_id,slug,name,email,address",
                                    prefix: "false,true,true,false,true,true,true",
                                    num_typos: "0,2,1,0,2,2,2",
                                },
                                {
                                    collection: this.searchPrefix + "users",
                                    q: query,
                                    query_by: "*",
                                },
                                {
                                    collection: this.searchPrefix + "leads",
                                    q: query,
                                    query_by:
                                        "phone,prefixed_id,email,prefixed_id,name",
                                    prefix: "false,true,true,true,true",
                                    num_typos: "0,1,2,2,2",
                                },
                                {
                                    collection:
                                        this.searchPrefix +
                                        "invoice_projection",
                                    q: query,
                                    query_by: "*",
                                },
                                {
                                    collection: this.searchPrefix + "courses",
                                    q: query,
                                    query_by: "*",
                                },
                                {
                                    collection: this.searchPrefix + "cars",
                                    q: query,
                                    query_by: "*",
                                },
                                {
                                    collection:
                                        this.searchPrefix +
                                        "nohe_partner_certificates",
                                    q: query,
                                    query_by: "phone,birthday,slug,name,email",
                                    prefix: "false,true,false,true,true",
                                    num_typos: "0,2,0,2,2",
                                },
                            ],
                        });

                    if (this._destroyed) return;

                    this.results = response.results
                        .flatMap((result) => result?.hits ?? [])
                        .map((hit) => ({
                            ...hit.document,
                            highlights: hit.highlights ?? [],
                        }));
                } catch (e) {
                    if (this._destroyed) return;
                    console.error("Search error:", e);
                    this.results = [];
                }

                if (!this._destroyed) {
                    this.isLoading = false;
                }
            },

            async open() {
                this.isOpen = true;
                let pageContext = null;
                if (this.contextSearchEnabled) {
                    try {
                        pageContext = await this.$wire.getSpotlightPageContext(
                            window.location.pathname || "/",
                        );
                    } catch (e) {
                        console.error(e);
                    }
                }
                if (pageContext) {
                    await this.selectEntity({
                        type: pageContext.type,
                        id: pageContext.id,
                        name: pageContext.name,
                    });
                }
                this.$nextTick(() => {
                    this.$refs.input?.focus();
                });
            },

            close() {
                this.isOpen = false;
                this.query = "";
                this.results = [];
                this.appResults = [];
                this.selectedIndex = 0;
                this.selectedUser = null;
                this.userMenuItems = [];
                this.showUserMenu = false;
            },

            async toggle() {
                if (this.isOpen) {
                    this.close();
                } else {
                    await this.open();
                }
            },

            async selectEntity(item) {
                this.selectedUser = item;
                this.isLoading = true;

                try {
                    const menuItems =
                        item.type === "student"
                            ? await this.$wire.getStudentMenu(item.id)
                            : await this.$wire.getUserMenu(item.id);

                    this.userMenuItems = menuItems.map((menuItem) => ({
                        ...menuItem,
                        type: "entity-menu",
                    }));
                    this.showUserMenu = true;
                    this.query = "";
                    this.selectedIndex = 0;
                } catch (e) {
                    console.error("Error loading menu:", e);
                }

                this.isLoading = false;
                this.$nextTick(() => {
                    this.$refs.input?.focus();
                });
            },

            backToSearch() {
                this.showUserMenu = false;
                this.selectedUser = null;
                this.userMenuItems = [];
                this.query = "";
                this.selectedIndex = 0;
                this.$nextTick(() => {
                    this.$refs.input?.focus();
                });
            },

            handleBackspace(event) {
                if (this.showUserMenu && this.query === "") {
                    event.preventDefault();
                    this.backToSearch();
                }
            },

            handleEscape() {
                if (this.showUserMenu) {
                    this.backToSearch();
                } else {
                    this.close();
                }
            },

            selectUp() {
                this.selectedIndex = Math.max(0, this.selectedIndex - 1);
                this.scrollToSelected();
            },

            selectDown() {
                this.selectedIndex = Math.min(
                    (this.allResults?.length ?? 1) - 1,
                    this.selectedIndex + 1,
                );
                this.scrollToSelected();
            },

            scrollToSelected() {
                this.$nextTick(() => {
                    const list = this.$refs.results;
                    if (!list) return;
                    const items = list.querySelectorAll("li");
                    items[this.selectedIndex]?.scrollIntoView({
                        block: "nearest",
                    });
                });
            },

            async go(item = null) {
                const target = item ?? this.allResults[this.selectedIndex];
                if (!target) return;

                if (target.type === "user" || target.type === "student") {
                    await this.selectEntity(target);
                    return;
                }

                const link = target.link || target.url;
                if (link) {
                    if (target.external) {
                        window.open(link, "_blank", "noopener,noreferrer");
                    } else if (target.livewireNavigate === false) {
                        window.location.href = link;
                    } else {
                        Livewire.navigate(link);
                    }

                    this.close();
                }
            },

            getHighlight(item, field) {
                if (item.type === "app") return null;
                const highlight = item.highlights?.find(
                    (h) => h.field === field,
                );
                return highlight?.snippet ?? null;
            },

            getDisplayName(item) {
                if (item.type === "app") return item.name;
                return (
                    this.getHighlight(item, "name") ||
                    item.name ||
                    "(kein Name)"
                );
            },

            getSpotlightSlugLabel(item) {
                if (item.type === "lead") {
                    return null;
                }
                if (!item.slug) {
                    return null;
                }
                if (Array.isArray(item.slug)) {
                    const raw = item.slug[1] ?? item.slug[0];
                    if (raw === undefined || raw === null || raw === "") {
                        return null;
                    }

                    return `#${String(raw).replace(/^#/, "")}`;
                }

                return `#${String(item.slug).replace(/^#/, "")}`;
            },

            getSecondaryFieldsHtml(item) {
                if (item.type === "app") return "";

                const highlightedFields = (item.highlights ?? [])
                    .map((h) => h.field)
                    .filter((f) => f !== "name" && f !== "slug");

                const html = highlightedFields
                    .slice(0, 2)
                    .map((field) => {
                        const value =
                            this.getHighlight(item, field) || item[field] || "";
                        const displayValue = Array.isArray(value)
                            ? value[0]
                            : value;
                        const prefix =
                            field === "birthday"
                                ? "Geb. "
                                : field === "phone"
                                  ? "Tel. "
                                  : "";
                        const fullValue = prefix + displayValue;
                        return fullValue
                            ? `<span class="whitespace-nowrap">${fullValue}</span>`
                            : "";
                    })
                    .filter(Boolean)
                    .join("");

                if (!html && item.phone) {
                    const phone = Array.isArray(item.phone)
                        ? (item.phone[1] ?? item.phone[0])
                        : item.phone;
                    return `<span class="whitespace-nowrap">Tel. ${phone}</span>`;
                }

                return html;
            },

            getTypeLabel(type) {
                const labels = {
                    student: "Fahrschüler:in",
                    user: "Mitarbeiter:in",
                    lead: "Lead",
                    invoice: "Rechnung",
                    course: "Kurs",
                    car: "Fahrzeug",
                    "nohe-partner-certificate": "Zertifikat",
                    app: "Admin",
                    "entity-menu": "Seite",
                };
                return labels[type] ?? type;
            },

            getTypeColor(type) {
                const colors = {
                    student: "bg-blue-100 text-blue-700",
                    user: "bg-amber-100 text-amber-700",
                    lead: "bg-emerald-100 text-emerald-700",
                    invoice: "bg-violet-100 text-violet-700",
                    course: "bg-rose-100 text-rose-700",
                    car: "bg-slate-100 text-slate-700",
                    "nohe-partner-certificate": "bg-teal-100 text-teal-700",
                    app: "bg-blink-100 text-blink-700",
                    "entity-menu": "bg-gray-50 text-gray-600",
                };
                return colors[type] ?? "bg-gray-100 text-gray-700";
            },

            getItemKey(item, index) {
                if (!item) return `empty-${index}`;
                return `${item.type ?? "unknown"}-${item.id || item.link || index}`;
            },
        };
    },
);
